import React from 'react';
import { Controller } from 'react-hook-form';

import { Box, TextField, Typography, FormControl, Autocomplete } from '@mui/material';

interface AutocompleteOption {
  value: string | number;
  label: string;
  email?: string;
  contactNo?: string;
}

interface AutocompleteCreatableSelectFieldProps {
  name: string;
  control: any;
  options: AutocompleteOption[];
  label: string;
  placeholder: string;
  helperText?: string;
  disabled?: boolean;
  showValueInLabel?: boolean;
  onChangeCallback?: (value: string | number) => void;
}

const AutocompleteCreatableSelectField: React.FC<AutocompleteCreatableSelectFieldProps> = ({
  name,
  control,
  options,
  label,
  placeholder,
  helperText,
  disabled = false,
  showValueInLabel = false,
  onChangeCallback,
}) => {

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field, fieldState }) => {
        const error = !!fieldState.error;
        const finalHelperText = fieldState.error?.message || helperText;

        // Find selected option if it's an object, otherwise use the raw string
        const selectedOption =
          options.find((opt) => opt.value === field.value) || field.value || '';

        return (
          <FormControl fullWidth error={error}>
            <Autocomplete
              freeSolo
              disabled={disabled}
              options={options}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                const labelText = showValueInLabel
                  ? `${option.label} (${option.value})`
                  : option.label;
                const email = option.email ? `📧 ${option.email}` : '';
                const contact = option.contactNo ? `📞 ${option.contactNo}` : '';
                return [labelText, email, contact].filter(Boolean).join(' ');
              }}
              isOptionEqualToValue={(option, value) =>
                typeof value === 'object' ? option.value === value.value : option.label === value
              }
              filterOptions={(opts, { inputValue }) =>
                opts.filter((option) => {
                  const searchText = inputValue.toLowerCase();
                  return (
                    option.label.toLowerCase().includes(searchText) ||
                    option.email?.toLowerCase().includes(searchText) ||
                    option.contactNo?.toLowerCase().includes(searchText)
                  );
                })
              }
              value={
                typeof field.value === 'string'
                  ? { label: field.value, value: field.value }
                  : options.find((opt) => opt.value === field.value) || null
              }
              onChange={(_, selected) => {
                if (typeof selected === 'string') {
                  field.onChange(selected);
                  onChangeCallback?.(selected);
                } else if (selected) {
                  field.onChange(selected.value);
                  onChangeCallback?.(selected.value);
                } else {
                  field.onChange('');
                  onChangeCallback?.('');
                }
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  // Optional: you may skip this if you're only using onChange
                  field.onChange(newInputValue);
                  onChangeCallback?.(newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={placeholder}
                  helperText={finalHelperText}
                  error={error}
                  sx={{
                    // Target the main input element's background
                    '& .MuiInputBase-input': {
                      backgroundColor: 'white', 
                    },
                    // Target the container of the input/select (if needed)
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.email && (
                      <Typography variant="body2" color="text.secondary">
                        📧 {option.email}
                      </Typography>
                    )}
                    {option.contactNo && (
                      <Typography variant="body2" color="text.secondary">
                        📞 {option.contactNo}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
            />
          </FormControl>
        );
      }}
    />
  );
};

export default AutocompleteCreatableSelectField;
